import { useMemo } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import AddAssignedTag from "components/assigned-tags/add-assigned-tag.component";
import { TagTypes } from "constants/tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

type PatientTagsFieldProps = {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  sx?: SxProps<Theme>;
};

export default function PatientTagsField({
  value,
  onChange,
  sx
}: PatientTagsFieldProps) {
  const { palette } = useTheme();

  const selectedAssignedTags = useMemo<AssignedTag[]>(
    () => value.map((tag) => ({ id: tag.id, object_id: 0, tag })),
    [value]
  );

  const handleAdd = (_tagId: number, tag: Tag) => {
    if (value.some((selectedTag) => selectedTag.id === tag.id)) return;
    onChange([...value, tag]);
  };

  const handleDelete = (tagId: number) => {
    onChange(value.filter((tag) => tag.id !== tagId));
  };

  return (
    <Box sx={sx}>
      <Typography
        component="div"
        variant="body2"
        sx={{ mb: 0.75, color: "text.secondary" }}
      >
        Tags
      </Typography>
      <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap" }}>
        {value.map((tag) => (
          <Chip
            key={tag.id}
            label={tag.name}
            size="small"
            onDelete={() => handleDelete(tag.id)}
            sx={{
              backgroundColor: tag.color,
              color: palette.getContrastText(tag.color),
              mb: 0.75,
              mr: 1,
              "& .MuiChip-deleteIcon": {
                color: "rgba(255,255,255,0.7)"
              }
            }}
          />
        ))}
        <AddAssignedTag
          tagType={TagTypes.PATIENT}
          existingTags={selectedAssignedTags}
          onAdd={handleAdd}
          filterActiveOnly
        />
      </Stack>
    </Box>
  );
}
